import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components/general/Table"
import { MciTableType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import { VehicleCard } from "../user/VehicleCard"
import Swal from "sweetalert2"
import { convertEquipmentType } from "@/lib/services"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { MCI_Table: MciTableType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: MciTableType) => void
  handleFormUpdate: (form: MciTableType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const MciTable: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<MciTableType>()

  const onSubmit = (data: MciTableType) => {
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      // color: "#ffffff",
      // background: "#333333",
      showCancelButton: true,
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: MciTableType) => {
    props.setTitle('Update MCI')
    setValue("id", e.id)
    setValue("equipmentType", e.equipmentType)
    setValue("datLowMod", e.datLowMod)
    setValue("datAvgMod", e.datAvgMod)
    setValue("datHighMod", e.datHighMod)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      equipmentType: "",
      datLowMod: undefined,
      datAvgMod: undefined,
      datHighMod: undefined,
      id: undefined,
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title={props.title}
      >
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card"
                value="VAN"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card">
                <VehicleCard image="/images/dry-van.svg" name="Dry Van" />
              </label>
            </div>
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card1"
                value="FLATBED"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card1">
                <VehicleCard image="/images/flatbed.svg" name="Flatbed" />
              </label>
            </div>
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card2"
                value="REEFER"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card2">
                <VehicleCard image="/images/reefer.svg" name="Reefer" />
              </label>
            </div>
          </div>
          <div className="flex flex-col gap-3 mt-3">
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Dat Low Mod</label>
                <input
                  className={
                    (errors.datLowMod ? "border-red-400" : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter Dat Low Mod"
                  {...register("datLowMod", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Dat Avg Mod</label>
                <input
                  className={
                    (errors.datAvgMod ? "border-red-400" : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter Dat Avg Mod"
                  {...register("datAvgMod", { required: true })}
                />
              </fieldset>
            </div>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Dat High Mod</label>
              <input
                className={
                  (errors.datHighMod ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Dat High Mod"
                {...register("datHighMod", { required: true })}
              />
            </fieldset>
          </div>
          <button
            onClick={handleSubmit(onSubmit)}
            className=" bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.MCI_Table.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-52"}>
                {convertEquipmentType(e.equipmentType)}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.datLowMod}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.datAvgMod}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                <p>{e.datHighMod}</p>
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    onClick={() => populateData(e)}
                    className="w-4 h-4 text-accent cursor-pointer"
                  />
                  <TrashIcon
                    onClick={() => onDelete(e.id)}
                    className="w-4 h-4 text-accent cursor-pointer"
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
